import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { ApiBotType } from '@/modules/api';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { sliceTwins } from '@/pages/Onboarding/utils/sliceTwins';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';

import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import './Email.scss';

export const OnboardingReplikaEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();
  }, []);

  const { refreshUser } = useAccount();

  const form = useFormContext<LayoutDataType>();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formCharacters = form.watch('characters');
  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const [twinsData, setTwinsData] = React.useState<ApiBotType[]>(
    formCharacters || [],
  );
  const sicedTwins = sliceTwins(twinsData || []);

  useQueryPersonalizedAis({
    enabled: !twinsData.length || !formCharacters.length,
    form,
    onSuccess: (data) => {
      setTwinsData(data.twins);
    },
  });

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      Promise.all([
        createStripeClient(lowerCaseEmail),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ])
        .then(() => {
          trackOnboardingComplete({ email: lowerCaseEmail });
          refreshUser();
        })
        .catch((error) => {
          console.error('Error during creating stripe client', error);
        });
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  return (
    <OnboardingPage className="onboarding-email">
      <OnboardingNavigation />

      <IonContent>
        <OnboardingTitle className="onboarding-email-title">
          We’ve added{' '}
          <UiTypography variant="inherit" color="danger" component="span">
            1578
          </UiTypography>{' '}
          AI characters
          <br />
          customized to your preferences
        </OnboardingTitle>

        <div className="images-container">
          <div className="images-view">
            {sicedTwins.map((twinArr, idx) => (
              <div className="images-view-line" key={idx}>
                {twinArr.map((twin) => (
                  <img src={twin.profile_picture_url} alt="AI" key={twin.id} />
                ))}
              </div>
            ))}
          </div>
        </div>
        <OnboardingTitle className="email-title">
          Enter your email to create a personal account
        </OnboardingTitle>

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            disableButton={!isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
            loading={isCreatingStripeClient}
          />
        </div>
        <div className="email-protection">
          <InfoProtection />
        </div>
      </IonContent>
    </OnboardingPage>
  );
};
